import { useState } from 'react';
import { Copy, Check, ArrowUpRight, Bot, Code2 } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { PageHeader, Card, cx } from '../components/ui';

// Native, readable developer page (PRD §9.4): connection details + explicit LLM/MCP guidance + a
// clean endpoint reference generated from the live OpenAPI doc. The full interactive "try it" console
// lives at the public docs site (linked) — embedding it cramped looked bad, so we link out instead.
const apiBase = import.meta.env.VITE_API_BASE_URL ?? 'https://api.runpavlov.ai';
const DOCS_URL = 'https://pavlov-docs.pages.dev';

interface OpenApiOp {
  summary?: string;
  operationId?: string;
  tags?: string[];
}
interface OpenApiSpec {
  paths: Record<string, Record<string, OpenApiOp>>;
}
const METHODS = ['get', 'post', 'patch', 'put', 'delete'];

function Copyable({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted">{label}</span>
      <code className="flex-1 truncate font-mono text-sm text-ink">{value}</code>
      <button
        onClick={() => void navigator.clipboard?.writeText(value).then(() => setCopied(true))}
        className="shrink-0 text-muted transition-colors hover:text-ink"
        title="Copy"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-input border border-line bg-ink/[0.02] p-4 font-mono text-xs leading-relaxed text-ink">
      {children}
    </pre>
  );
}

function EndpointReference() {
  const { data } = useLoad(() => api.get<OpenApiSpec>('/openapi.json'));
  if (!data) return <Card className="p-5 text-sm text-muted">Loading endpoints…</Card>;

  const groups: Record<string, { method: string; path: string; summary: string }[]> = {};
  for (const [path, ops] of Object.entries(data.paths)) {
    for (const [method, op] of Object.entries(ops)) {
      if (!METHODS.includes(method)) continue;
      const tag = op.tags?.[0] ?? 'Other';
      (groups[tag] ??= []).push({
        method: method.toUpperCase(),
        path,
        summary: op.summary ?? op.operationId ?? '',
      });
    }
  }
  const tags = Object.keys(groups).sort();

  return (
    <div className="space-y-6">
      {tags.map((tag) => (
        <div key={tag}>
          <div className="eyebrow mb-2">{tag}</div>
          <Card className="divide-y divide-line">
            {groups[tag]!.map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className={cx(
                    'w-14 shrink-0 rounded-md px-2 py-0.5 text-center font-mono text-[11px] font-semibold',
                    e.method === 'GET' ? 'border border-line text-muted' : 'bg-ink text-paper',
                  )}
                >
                  {e.method}
                </span>
                <code className="shrink-0 font-mono text-xs text-ink">{e.path}</code>
                <span className="truncate text-xs text-muted">{e.summary}</span>
              </div>
            ))}
          </Card>
        </div>
      ))}
    </div>
  );
}

export function ApiDocsPage() {
  return (
    <div>
      <PageHeader
        title="API & MCP"
        subtitle="Everything you — or your AI agents — need to build on Pavlov."
        actions={
          <a href={DOCS_URL} target="_blank" rel="noreferrer" className="btn-primary">
            Full interactive reference <ArrowUpRight className="h-4 w-4" />
          </a>
        }
      />

      <div className="eyebrow mb-3">Connect</div>
      <Card className="mb-8 divide-y divide-line">
        <Copyable label="API base" value={apiBase} />
        <Copyable label="OpenAPI" value={`${apiBase}/openapi.json`} />
        <Copyable label="MCP" value={`${apiBase}/mcp`} />
        <Copyable label="llms.txt" value={`${apiBase}/llms.txt`} />
      </Card>

      <div className="eyebrow mb-3">Point AI tools here</div>
      <div className="mb-8 grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <div className="flex items-center gap-2 font-display text-lg text-ink">
            <Bot className="h-5 w-5" /> MCP clients (Claude, Cursor…)
          </div>
          <p className="mt-1 text-sm text-muted">
            Add an MCP server pointing at <code className="font-mono text-ink">/mcp</code> with your
            API key — agents get the full set-up → run → observe toolset.
          </p>
          <Code>{`{
  "mcpServers": {
    "pavlov": {
      "url": "${apiBase}/mcp",
      "headers": { "Authorization": "Bearer sk_live_..." }
    }
  }
}`}</Code>
          <p className="mt-2 text-xs text-muted">
            Discover tools with <code className="font-mono">tools/list</code>: create_org,
            attest_org, create_target_list, import_recipients, create_campaign, launch_campaign,
            list_events…
          </p>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 font-display text-lg text-ink">
            <Code2 className="h-5 w-5" /> LLMs (context / codegen)
          </div>
          <p className="mt-1 text-sm text-muted">
            Feed the concise <code className="font-mono text-ink">llms.txt</code> summary or the
            full OpenAPI contract to any model or codegen tool.
          </p>
          <Code>{`# concise guide for LLMs
curl ${apiBase}/llms.txt

# full machine-readable contract (SDKs, tool defs)
curl ${apiBase}/openapi.json`}</Code>
          <p className="mt-2 text-xs text-muted">
            The OpenAPI document is the frozen contract — ideal for SDK generation and tool
            definitions.
          </p>
        </Card>
      </div>

      <div className="eyebrow mb-3">Authentication</div>
      <Card className="mb-8 p-5">
        <p className="text-sm text-muted">
          Every request carries a Bearer key (create + rotate under{' '}
          <span className="text-ink">API / MCP</span>; the secret is shown once). Writes also need
          an <code className="font-mono text-ink">Idempotency-Key</code>.
        </p>
        <Code>{`curl ${apiBase}/v1/campaigns \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "content-type: application/json" \\
  -d '{ "org_id": "org_...", "template_id": "tpl_...", "sender_profile_id": "snd_...", "target_list_id": "list_..." }'`}</Code>
      </Card>

      <div className="mb-3 flex items-center justify-between">
        <div className="eyebrow">Endpoints</div>
        <a
          href={DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-muted hover:text-ink"
        >
          Try them interactively ↗
        </a>
      </div>
      <EndpointReference />
    </div>
  );
}
